import React from 'react'
import { Card, Typography, Button, Badge, Image } from 'antd'
import { useCartStore } from '../state/cartStore'
import Decimal from 'decimal.js-light'
import { formatAmount } from '../utils/amount'
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";

// Reusable product card component for priced items
// Expects an object shaped like the API response entries under each section key
// {
//   id, sellPrice, sellPriceBeforeDiscount, item: { imageUrl, nameAr }
// }
export default function ProductCard({ pricedItem, onClick }) {
  const addItem = useCartStore(s => s.addItem)
  const setQty = useCartStore(s => s.setQty)
  const item = pricedItem?.item || {}
  const title = pricedItem?.nameAr || item?.nameAr || item?.name || ''
  const showIds = typeof localStorage !== 'undefined' && localStorage.getItem('showIds') === 'true'

  let imgSrc = `https://images.leen-go.com/${pricedItem?.imageUrl || item?.imageUrl}` || 'no-image.png'
  const price = pricedItem?.sellPrice ?? null
  const oldPrice = pricedItem?.sellPriceBeforeDiscount ?? null

  const mapId = pricedItem?.id
  const key = mapId ? `map:${mapId}` : null
  const qty = useCartStore(React.useCallback(s => (key ? (s.items?.[key]?.quantity || 0) : 0), [key]))

  // shopClosed: the shop that owns this item is currently closed (unified catalog case)
  const shopClosed = pricedItem?.shopClosed === true || pricedItem?.shop?.isOpened === false
  const unavailable = pricedItem?.available === false || shopClosed
  // Bulk promo detection
  const bulkMin = Number(pricedItem?.bulkMinQuantity)
  const hasBulk = !unavailable && Number.isFinite(bulkMin) && bulkMin > 0 && pricedItem?.bulkSellPrice != null
  const bulkRibbonText = (() => {
    if (!hasBulk) return null
    try {
      const total = new Decimal(pricedItem?.bulkSellPrice || 0).times(bulkMin)
      const formatted = formatAmount(Number(total.toFixed(2)))
      return `كل ${bulkMin} ب ${formatted}`
    } catch {
      // Fallback without Decimal formatting
      const total = (Number(pricedItem?.bulkSellPrice) || 0) * bulkMin
      return `كل ${bulkMin} ب ${formatAmount(total)}`
    }
  })()

  const inc = () => { if (!unavailable) addItem(pricedItem) }
  const dec = () => { if (!key || unavailable) return; setQty(key, qty - 1) }

  const card = (
    <Card
      hoverable={!unavailable}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        filter: unavailable ? 'grayscale(100%)' : undefined,
        opacity: unavailable ? 0.6 : 1,
        flex: 1,
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: unavailable ? 'not-allowed' : (onClick ? 'pointer' : 'default'),
      }}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '12px 8px'
      }}
      cover={
        <div
          style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', background: '#fafafa' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Image
            src={imgSrc}
            alt={title || 'منتج'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            loading="lazy"
            fallback="/no-image.png"
            preview={{
              mask: <div style={{ fontSize: 12 }}>تكبير</div>
            }}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        {showIds && pricedItem?.id != null && (
          <span
            style={{
              display: 'inline-block',
              fontSize: 16,
              fontWeight: 'bold',
              fontFamily: 'monospace',
              background: '#f0f0f0',
              color: '#666',
              padding: '2px 6px',
              borderRadius: 4,
              marginBottom: 4
            }}
          >
            #{pricedItem.id}
          </span>
        )}
        <Typography.Paragraph
          title={title}
          style={{
            marginBottom: 0,
            whiteSpace: 'normal',
            fontSize: 14,
            lineHeight: '1.2em',
            wordBreak: 'break-word',
            textAlign: 'start',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            WebkitLineClamp: 2,
            minHeight: '2.4em'
          }}
        >
          {title || '—'}
        </Typography.Paragraph>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 'auto' }}>
          {price != null && (
            <Typography.Text style={{ fontSize: 16 }} strong>{formatAmount(price)}</Typography.Text>
          )}
          {oldPrice != null && oldPrice !== price && (
            <Typography.Text delete type="secondary" style={{ fontSize: 12 }}>
              {formatAmount(oldPrice)}
            </Typography.Text>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginTop: 4 }}>
          <Button size="small" icon={<MinusOutlined />} onClick={(e) => { e.stopPropagation?.(); dec(); }} disabled={!key || qty <= 0 || unavailable}></Button>
          <Typography.Text style={{ fontSize: 13 }}>{qty}</Typography.Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation?.(); inc(); }} disabled={unavailable}></Button>
        </div>
      </div>
    </Card>
  )

  if (shopClosed) {
    return <Badge.Ribbon text="المتجر مغلق" color="volcano" rootStyle={{ display: 'flex', flex: 1, width: '100%' }}>{card}</Badge.Ribbon>
  }
  if (unavailable) {
    return <Badge.Ribbon text="غير متوفر حاليا" color="red" rootStyle={{ display: 'flex', flex: 1, width: '100%' }}>{card}</Badge.Ribbon>
  }
  if (hasBulk && bulkRibbonText) {
    return <Badge.Ribbon text={bulkRibbonText} color="#f55905" rootStyle={{ display: 'flex', flex: 1, width: '100%' }}>{card}</Badge.Ribbon>
  }
  return <div style={{ display: 'flex', flex: 1, width: '100%' }}>{card}</div>
}
